<template>
  <div>
    <h5 class="mb-4"><strong>Confirm this award</strong></h5>

    <b-alert v-if="errorMessage" variant="danger" show class="mb-3">
      {{ errorMessage }}
    </b-alert>

    <div class="section-label">Recipients ({{ employees.length }})</div>
    <div class="recipient-list">
      <div v-for="emp in employees" :key="emp.employee_id" class="recipient-row">
        <div class="recipient-row__text">
          <span class="recipient-row__name">{{ emp.full_name }}</span>
          <!-- What the picker showed wins: anyone ambiguous there is still ambiguous
               here. Everyone else keeps the plain division this row always had. -->
          <span v-if="subtitleFor(emp)" class="recipient-row__subtitle">{{ subtitleFor(emp) }}</span>
        </div>
        <div class="recipient-row__right">
          <span v-if="amountPerPersonMinor" class="recipient-row__amount">
            {{ formatAmount(amountPerPersonMinor) }}
          </span>
          <font-awesome-icon
            v-if="employees.length > 1"
            :icon="['fal', 'xmark']"
            class="recipient-row__remove"
            role="button"
            @click="$emit('remove', emp)"
          />
        </div>
      </div>
    </div>

    <div v-if="amountPerPersonMinor" class="totals mt-3">
      <div class="totals__row">
        <span>Per person</span>
        <span>{{ formatAmount(amountPerPersonMinor) }}</span>
      </div>
      <div class="totals__row totals__row--total">
        <span>Total</span>
        <span>{{ formatAmount(totalMinor) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { toCurrency } from '@/utils';

const props = defineProps({
  employees: {
    type: Array,
    default: () => [],
  },
  amountPerPersonMinor: {
    type: Number,
    default: null,
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

defineEmits(['remove']);

const totalMinor = computed(() => (props.amountPerPersonMinor || 0) * props.employees.length);

function subtitleFor(employee) {
  return employee.picker_subtitle || employee.division_name || '';
}

function formatAmount(amount) {
  return toCurrency(amount);
}
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/give-recognition';

.recipient-list {
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 0.5rem;
  max-height: 15rem;
  overflow-y: auto;
}

.recipient-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.75rem;

  &:not(:last-child) {
    border-bottom: 1px solid var(--palette-color-default-lighten-95);
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    font-weight: 500;
    font-size: 0.875rem;
  }

  &__subtitle {
    font-size: 0.8rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__right {
    align-items: center;
    display: flex;
    gap: 0.75rem;
  }

  &__amount {
    font-size: 0.875rem;
    font-weight: 500;
  }

  &__remove {
    cursor: pointer;
    opacity: 0.6;

    &:hover {
      opacity: 1;
    }
  }
}

.totals {
  &__row {
    display: flex;
    justify-content: space-between;
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-30);
    padding: 0.25rem 0;
  }

  &__row--total {
    border-top: 1px solid var(--palette-color-default-lighten-90);
    color: var(--palette-color-default);
    font-weight: 600;
    margin-top: 0.25rem;
    padding-top: 0.5rem;
  }
}
</style>
